import {
    type ContractVariant,
    widgetPunctualityCharttooltipContract,
} from 'aos-ds/src/patterns/widget-punctuality-charttooltip/widget-punctuality-charttooltip.contract'
import { Typography } from 'aos-ds/src/typography/Typography'
import { variantForStyleId } from 'aos-ds/src/typography/variants'
import React from 'react'
import { View } from 'react-native'

import { twMerge } from '../../tailwind/merge'
import {
    PunctualityChartTooltipDirection,
    type PunctualityChartTooltipDirectionData,
} from '../punctuality-chart-tooltip-direction'
import type { PunctualityChartTooltipUser } from '../punctuality-chart-tooltip-flight-row'

export const PUNCTUALITY_CHART_TOOLTIP_HOURS =
    widgetPunctualityCharttooltipContract.props.hour.values

export type PunctualityChartTooltipHour = (typeof PUNCTUALITY_CHART_TOOLTIP_HOURS)[number]

export type PunctualityChartTooltipProps = {
    hour: PunctualityChartTooltipHour
    user: PunctualityChartTooltipUser
    title: string
    departures: PunctualityChartTooltipDirectionData
    arrivals: PunctualityChartTooltipDirectionData
    className?: string
    testID?: string
}

export const punctualityChartTooltipKey = (
    hour: PunctualityChartTooltipHour,
    user: PunctualityChartTooltipUser,
): string => `hour=${hour}, user=${user}`

export const resolvePunctualityChartTooltip = (
    hour: PunctualityChartTooltipHour,
    user: PunctualityChartTooltipUser,
): ContractVariant => {
    const key = punctualityChartTooltipKey(hour, user)
    const cell = (
        widgetPunctualityCharttooltipContract.variants as Record<string, ContractVariant>
    )[key]
    if (!cell) {
        throw new Error(`PunctualityChartTooltip: the Figma contract has no "${key}"`)
    }
    return cell
}

const partOf = (cell: ContractVariant, name: string): string => {
    const found = cell.parts[name]
    if (found === undefined) {
        throw new Error(`PunctualityChartTooltip: the Figma contract has no part "${name}"`)
    }
    return found
}

const titleVariantOf = (cell: ContractVariant) => {
    const style = cell.partTextStyles.title
    if (style === undefined) {
        throw new Error('PunctualityChartTooltip: the Figma contract has no text style for "title"')
    }
    return variantForStyleId(style)
}

/**
 * Figma `Widget/Punctuality/ChartTooltip` (19022:460108): the hover card for one
 * hour of the on-time line — the hour's title, then Departures and Arrivals
 * apart, divided by a 1px rule. Its own surface, not an instance of the aos-ds
 * `Tooltip`: the frame binds the same shell (`background/elevation`, `common`,
 * `small-compact/s`, `small-compact/xs`) and a fixed 280 width.
 *
 * `hour` picks the cell; each direction carries its own state, so a forecast
 * hour can hold a direction without prediction. `user=external` hides the
 * delay codes in every row.
 */
export const PunctualityChartTooltip = ({
    hour,
    user,
    title,
    departures,
    arrivals,
    className,
    testID = 'punctuality-chart-tooltip',
}: PunctualityChartTooltipProps) => {
    const cell = resolvePunctualityChartTooltip(hour, user)
    return (
        <View className={twMerge(cell.container, className)} testID={testID}>
            <Typography
                variant={titleVariantOf(cell)}
                className={twMerge('w-full', cell.label, partOf(cell, 'title'))}
                testID={`${testID}-title`}
            >
                {title}
            </Typography>
            <PunctualityChartTooltipDirection
                {...departures}
                direction='departures'
                user={user}
                className={partOf(cell, 'departures')}
                testID={`${testID}-departures`}
            />
            <View
                className={twMerge('w-full', partOf(cell, 'divider'))}
                testID={`${testID}-divider`}
            />
            <PunctualityChartTooltipDirection
                {...arrivals}
                direction='arrivals'
                user={user}
                className={partOf(cell, 'arrivals')}
                testID={`${testID}-arrivals`}
            />
        </View>
    )
}

PunctualityChartTooltip.displayName = 'PunctualityChartTooltip'
